// Presence: who else has the board open and where their pointer is. Ephemeral – relayed by the
// server (PRESENCE frames), never stored. Name and short name come from the account, or – without
// accounts – are picked once per browser (localStorage, see account.ts).
// Presentation mode (E3) rides along: a presenter sends the board rect they see (`view`), the others
// follow it and say whom they follow (`follow`), so the presenter can count them.

export interface Me { name: string; short: string }
/** Board rect `[x, y, w, h]`. */
export type View = [number, number, number, number];
export interface Peer {
  id: number; name: string; short: string; x: number | null; y: number | null; seen: number;
  /** The rect this peer presents (null = not presenting) and the presenter it follows. */
  view: View | null; follow: number | null;
}

interface Wire { peer: number; name: string; short: string; x: number | null; y: number | null; leave?: boolean; view?: View | null; follow?: number | null }

/** A presented rect from the wire: four finite numbers, positive size; anything else is none. */
export function cleanView(v: unknown): View | null {
  if (!Array.isArray(v) || v.length !== 4 || !v.every((n) => typeof n === "number" && Number.isFinite(n))) return null;
  const [x, y, w, h] = v as number[];
  return w > 0 && h > 0 && Math.abs(x) < 1e8 && Math.abs(y) < 1e8 && w < 1e8 && h < 1e8 ? [x, y, w, h] : null;
}

const KEY = "lanewerk.me";
/** A peer that has not sent anything for this long is considered gone. */
export const PEER_TIMEOUT = 15_000;
const HEARTBEAT = 5_000;
const THROTTLE = 50;

/** Short name rule (catalogue K6): two letters of the last name + two of the first ("Eneriko Jurkovic" → "JUEN"). */
export function shortOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[parts.length - 1].slice(0, 2) + parts[0].slice(0, 2)).toUpperCase();
  return (parts[0] ?? "").slice(0, 4).toUpperCase();
}

export function loadMe(): Me {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const m = JSON.parse(raw) as Me;
      if (m.name && m.short) return { name: String(m.name).slice(0, 60), short: String(m.short).slice(0, 4) };
    }
  } catch { /* private mode, blocked storage */ }
  const n = Math.floor(Math.random() * 90 + 10);
  return { name: `Gast ${n}`, short: `G${n}` };
}

export function saveMe(m: Me) {
  try { localStorage.setItem(KEY, JSON.stringify(m)); } catch { /* not kept – fine */ }
}

export class Presence {
  readonly peers = new Map<number, Peer>();
  private cursor: { x: number; y: number } | null = null;
  private view: View | null = null;
  private follow: number | null = null;
  private lastSent = 0;
  private lastAnswer = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private listeners = new Set<() => void>();

  /**
   * @param send hands an encoded presence payload to the sync client (dropped while offline)
   */
  constructor(readonly peerId: number, private send: (payload: Uint8Array) => void, public me: Me = loadMe(), private now: () => number = () => Date.now()) {
    setInterval(() => this.tick(), HEARTBEAT);
    addEventListener("pagehide", () => this.post({ leave: true }));
  }

  subscribe(fn: () => void) { this.listeners.add(fn); }
  private changed() { for (const l of this.listeners) l(); }

  /** New name or short name (already saved by the account menu): tell the others at once. */
  setMe(m: Me) {
    this.me = { name: m.name.trim().slice(0, 60) || this.me.name, short: (m.short.trim() || shortOf(m.name)).slice(0, 4).toUpperCase() };
    this.post();
    this.changed();
  }

  /** Pointer position on the board (null = pointer left the board). Throttled. */
  setCursor(p: { x: number; y: number } | null) {
    this.cursor = p;
    const wait = THROTTLE - (this.now() - this.lastSent);
    if (wait <= 0) this.post();
    else if (!this.timer) this.timer = setTimeout(() => { this.timer = null; this.post(); }, wait);
  }

  /** Start/stop presenting (null) or move the presented rect. Start and stop are sent at once. */
  setView(v: View | null) {
    const edge = (v === null) !== (this.view === null);
    this.view = v;
    if (edge) { this.post(); this.changed(); } else this.setCursor(this.cursor);
  }
  get presenting() { return this.view !== null; }

  /** The presenter this viewer follows (null = nobody). */
  setFollow(id: number | null) {
    if (id === this.follow) return;
    this.follow = id;
    this.post();
  }

  /** Called after (re)connecting: tell the others right away. */
  announce() { this.post(); }

  receive(payload: Uint8Array) {
    let w: Wire;
    try { w = JSON.parse(new TextDecoder().decode(payload)) as Wire; } catch { return; }
    if (typeof w.peer !== "number" || w.peer === this.peerId) return;
    if (w.leave) {
      if (this.peers.delete(w.peer)) this.changed();
      return;
    }
    const known = this.peers.has(w.peer);
    this.peers.set(w.peer, {
      id: w.peer, name: String(w.name ?? "").slice(0, 60), short: String(w.short ?? "").slice(0, 4),
      x: typeof w.x === "number" ? w.x : null, y: typeof w.y === "number" ? w.y : null, seen: this.now(),
      view: cleanView(w.view), follow: typeof w.follow === "number" ? w.follow : null,
    });
    // someone new: answer at once so they see us without waiting for the heartbeat
    if (!known && this.now() - this.lastAnswer > 1000) { this.lastAnswer = this.now(); this.post(); }
    this.changed();
  }

  private tick() {
    let gone = false;
    for (const [id, p] of this.peers) if (this.now() - p.seen > PEER_TIMEOUT) { this.peers.delete(id); gone = true; }
    if (gone) this.changed();
    if (this.now() - this.lastSent >= HEARTBEAT - 100) this.post();
  }

  private post(extra: Partial<Wire> = {}) {
    this.lastSent = this.now();
    const w: Wire = { peer: this.peerId, name: this.me.name, short: this.me.short, x: this.cursor?.x ?? null, y: this.cursor?.y ?? null, view: this.view, follow: this.follow, ...extra };
    this.send(new TextEncoder().encode(JSON.stringify(w)));
  }
}
